/**
 * Avoid "Dr. Dr. Name" when a WhatsApp template already has "Dr." / "Doctor"
 * immediately before a body placeholder and the resolved value also starts with it.
 */

const HONORIFIC_PREFIX_RE = /^\s*(dr\.?|doctor)\s+/i;
const HONORIFIC_DUPLICATE_RE = /(?:(?:dr|doctor)\.?\s+){2,}/gi;
const HONORIFIC_BEFORE_SLOT_RE = /(dr\.?|doctor)[\s:,-]*$/i;

const DOCTOR_NAME_CONTEXT_KEYS = new Set([
  'doctorname',
  'vendorname',
  'doctor',
  'vendor',
]);

export function isDoctorNameContextKey(key?: string | null): boolean {
  return DOCTOR_NAME_CONTEXT_KEYS.has(String(key || '').trim().toLowerCase());
}

export function stripLeadingDoctorHonorific(value?: string | null): string {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text.replace(HONORIFIC_PREFIX_RE, '').trim();
}

export function deduplicateDoctorPrefix(value?: string | null): string {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text.replace(HONORIFIC_DUPLICATE_RE, 'Dr. ').trim();
}

/** Strip WhatsApp markdown noise so " *Dr.* " still counts as honorific before {{n}}. */
export function normalizeTemplateSlice(text?: string | null): string {
  return String(text || '')
    .replace(/[\*_~`]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trimEnd();
}

export function templateHasDoctorHonorificBeforeSlot(bodyPreview?: string | null, slot?: number): boolean {
  const body = String(bodyPreview || '');
  const n = Number(slot);
  if (!body || !Number.isFinite(n) || n < 1) return false;

  const marker = `{{${n}}}`;
  let from = 0;
  while (from < body.length) {
    const idx = body.indexOf(marker, from);
    if (idx < 0) break;
    const before = normalizeTemplateSlice(body.slice(Math.max(0, idx - 40), idx));
    if (HONORIFIC_BEFORE_SLOT_RE.test(before)) return true;
    from = idx + marker.length;
  }
  return false;
}

export function shouldStripDoctorHonorific({
  bodyPreview,
  slot,
  contextKey = null,
}: {
  bodyPreview?: string | null;
  slot: number;
  contextKey?: string | null;
}): boolean {
  if (templateHasDoctorHonorificBeforeSlot(bodyPreview, slot)) return true;

  const body = String(bodyPreview || '').trim();
  if (!body) return true;
  if (isDoctorNameContextKey(contextKey)) return true;
  if (Number(slot) === 1 && /(?:dr\.?|doctor)/i.test(body)) return true;

  return false;
}

export function dedupeDoctorHonorificForTemplateValue({
  bodyPreview,
  slot,
  value,
  contextKey = null,
}: {
  bodyPreview?: string | null;
  slot: number;
  value?: string | null;
  contextKey?: string | null;
}): string {
  let text = value == null ? '' : String(value).trim();
  if (!text) return text;

  // Clean duplicate "Dr. Dr." inside value -> "Dr."
  text = deduplicateDoctorPrefix(text);

  // If value starts with Dr. or Doctor:
  if (HONORIFIC_PREFIX_RE.test(text)) {
    if (shouldStripDoctorHonorific({ bodyPreview, slot, contextKey })) {
      const stripped = stripLeadingDoctorHonorific(text);
      return stripped || text;
    }
  }

  return text;
}

export function applyDoctorHonorificDedupeToBodyValues({
  bodyPreview,
  slots,
  mappings,
  values,
}: {
  bodyPreview?: string | null;
  slots?: Array<{ slot?: number; contextKey?: string | null; key?: string | null }>;
  mappings?: Array<{ slot?: number; contextKey?: string | null; key?: string | null }>;
  values: string[];
}): string[] {
  if (!Array.isArray(values) || !values.length) return values;

  const items = slots || mappings;

  return values.map((value, index) => {
    const slotInfo = items?.[index] || {};
    const slot = Number(slotInfo.slot) || index + 1;
    const cleaned = dedupeDoctorHonorificForTemplateValue({
      bodyPreview,
      slot,
      value,
      contextKey: slotInfo.contextKey || slotInfo.key || null,
    });
    return cleanConsultationTypeRepetition(cleaned);
  });
}

const FULL_TEXT_DR_DEDUPE_RE = /(dr\.?|doctor)\s*([*_~`]*)\s*(?:dr\.?|doctor)\s+/gi;

export function cleanConsultationTypeRepetition(text?: string | null): string {
  if (!text) return '';
  let str = String(text);
  str = str.replace(/Instant\s+Video(?:\s+Consult(?:ation)?)?\s+consultation/gi, 'Instant Video Consultation');
  str = str.replace(/Online\s+Video(?:\s+Consult(?:ation)?)?\s+consultation/gi, 'Online Video Consultation');
  str = str.replace(/In-?Clinic(?:\s+Consult(?:ation)?)?\s+consultation/gi, 'In-Clinic Consultation');
  str = str.replace(/Instant\s+Video\s+Consult(?!\s*ation)/gi, 'Instant Video Consultation');
  return str;
}

export function dedupeDoctorHonorificInFullText(text?: string | null): string {
  if (!text) return '';
  let str = String(text);
  // Replaces "Dear Dr. *Dr. Name*" or "Dr. Dr. Name" -> "Dear Dr. *Name*" or "Dr. Name"
  str = str.replace(FULL_TEXT_DR_DEDUPE_RE, '$1 $2').trim();
  str = str.replace(/(?:(?:dr|doctor)\.?\s+){2,}/gi, 'Dr. ');
  return cleanConsultationTypeRepetition(str);
}
